import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  CheckCircleOutline as MarkPaidIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helper';

const InvoiceStatusUpdateDialog = ({
  open,
  onClose,
  onConfirm,
  invoice,
  action, // 'mark_paid' or 'cancel'
  loading = false
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setReason('');
      setError('');
    }
  }, [open]);

  // Calculate balance and check business rules
  const calculateBalance = () => {
    if (!invoice) return 0;
    const totalAmount = parseFloat(invoice.taxable_invoice_value || 0);
    const paidAmount = parseFloat(invoice.total_paid_amount || 0);
    return totalAmount - paidAmount;
  };

  const getBalanceWarning = () => {
    if (!invoice || action !== 'mark_paid') return null;
    
    const balance = calculateBalance();
    const totalAmount = parseFloat(invoice.taxable_invoice_value || 0);
    const balancePercentage = totalAmount > 0 ? (balance / totalAmount) * 100 : 0;
    
    if (balancePercentage > 30) {
      return {
        type: 'error',
        message: `Cannot mark as paid. Balance amount (${formatCurrency(balance)}) is ${balancePercentage.toFixed(1)}% of total amount, which exceeds the 30% limit.`
      };
    } else if (balance > 0) {
      return {
        type: 'warning',
        message: `Warning: Balance amount of ${formatCurrency(balance)} will be added as discount when marking as paid.`
      };
    }
    
    return null;
  };

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    // Check if mark as paid is blocked due to high balance
    const balanceWarning = getBalanceWarning();
    if (balanceWarning && balanceWarning.type === 'error') {
      setError(balanceWarning.message);
      return;
    }

    const statusData = {
      status: action === 'mark_paid' ? 'PD' : 'CN',
      reason: reason.trim()
    };

    onConfirm(statusData);
  };

  const getActionConfig = () => {
    if (action === 'mark_paid') {
      return {
        title: 'Mark Invoice as Paid',
        icon: <MarkPaidIcon sx={{ fontSize: 28, color: 'success.main' }} />,
        color: 'success',
        buttonText: 'Mark as Paid',
        description: 'This will change the invoice status to "Paid". This action cannot be undone.',
        reasonLabel: 'Payment confirmation details',
        reasonPlaceholder: 'e.g., Payment received via bank transfer on [date]'
      };
    } else {
      return {
        title: 'Cancel Invoice',
        icon: <CancelIcon sx={{ fontSize: 28, color: 'error.main' }} />,
        color: 'error',
        buttonText: 'Cancel Invoice',
        description: 'This will cancel the invoice permanently. This action cannot be undone.',
        reasonLabel: 'Cancellation reason',
        reasonPlaceholder: 'e.g., Customer requested cancellation, duplicate invoice, etc.'
      };
    }
  };

  const config = getActionConfig();


  return (
    <Dialog
      open={open && invoice !== null}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        {config.icon}
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {config.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invoice #{invoice?.invoice_number || 'N/A'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Invoice Info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Invoice Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customer: {invoice?.account_name || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Amount: {formatCurrency(invoice?.taxable_invoice_value || 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current Status: <Chip 
              label={invoice?.status_verbose || 'Unknown'} 
              size="small" 
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Typography>
          {action === 'mark_paid' && (
            <Typography variant="body2" color="text.secondary">
              Balance Amount: <strong>{formatCurrency(calculateBalance())}</strong>
            </Typography>
          )}
        </Box>

        {/* Balance Warning/Error */}
        {(() => {
          const balanceWarning = getBalanceWarning();
          if (balanceWarning) {
            return (
              <Alert 
                severity={balanceWarning.type} 
                icon={<WarningIcon />}
                sx={{ mb: 3 }}
              >
                {balanceWarning.message}
              </Alert>
            );
          }
          return (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 3 }}
            >
              {config.description}
            </Alert>
          );
        })()}

        {/* Reason Field */}
        <TextField
          fullWidth
          required
          multiline
          rows={4}
          label={config.reasonLabel}
          placeholder={config.reasonPlaceholder}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError('');
          }}
          error={!!error}
          helperText={error}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !reason.trim() || (getBalanceWarning()?.type === 'error')}
          variant="contained"
          color={config.color}
          startIcon={loading ? <CircularProgress size={16} /> : config.icon}
        >
          {loading ? 'Processing...' : config.buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceStatusUpdateDialog;
